import { ChangeEvent, useState, useRef } from 'react';
import { Box, Card, Title, Text, Input } from '@nimbus-ds/components';
import { FormField } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { ProductWeightsAndDimensions } from '@tiendanube/common';
import { AI_FEEDBACK_SCORE } from '@tiendanube/common/src/common/AiFeedbackConstants';
import { Domain, AiFeature } from '@tiendanube/common/src/enums';
import { Alert, Grid, InterfaceNameValue, Stack } from '@tiendanube/components';
import FeatureFlag from 'App/components/FeatureFlag';
import { AI_PRODUCT_FEEDBACK_ENABLED } from 'App/featuresFlags';
import HelpLink from 'App/HelpLink';
import {
  AiFeedbackModal,
  InputNumberNimbus,
  useResponsive,
} from 'commons/components';
import ActionableToast from 'commons/components/ActionableToast';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import { HELP_LINK_WEIGHT_CALCULATION_URL } from 'commons/constants';
import {
  useTranslationLanguage,
  useTranslationCommon,
  useAiFeedback,
  AiNavigationAttributes,
} from 'commons/hooks';
import { trackingAiGenerateUseContentClick } from 'domains/Ai/tracking';
import {
  OnChangeToAllVariants,
  RequiredAiProperties,
} from 'domains/Catalog/Products/pages/components/Variants/types';
import { useProductSizeWithAI } from './hooks';
import ApplyValuesToAllVariants from '../ApplyValuesToAllVariants';
import { AiLoadingState } from '../Categories/components';
import WeighInput from '../WeightInput';

import './ProductSizes.scss';

export type SizeField = 'weight' | 'depth' | 'width' | 'height';
interface ProductSizesProps {
  appearance?: 'column' | 'row';
  weight: string;
  depth: string;
  width: string;
  height: string;
  showAlertNoWeightAndDimension: boolean;
  onChange: (data: InterfaceNameValue) => void;
  onChangeToAllVariants?: OnChangeToAllVariants;
  requiredAiProperties: RequiredAiProperties;
  aiGeneratedWeightsAndDimensions?: ProductWeightsAndDimensions;
  aiNavigationAttributes?: AiNavigationAttributes;
  remainingAiUses?: number;
}

function ProductSizes({
  appearance = 'column',
  weight,
  depth,
  width,
  height,
  showAlertNoWeightAndDimension,
  onChange,
  onChangeToAllVariants,
  requiredAiProperties,
  aiGeneratedWeightsAndDimensions,
  aiNavigationAttributes,
  remainingAiUses,
}: Readonly<ProductSizesProps>): JSX.Element {
  const { t } = useTranslation(Domain.CATALOG);
  const tCommon = useTranslationCommon();
  const language = useTranslationLanguage();
  const { isMobile } = useResponsive();
  const [isShowAlert, setIsShowAlert] = useState(false);
  const isRowAppearance = appearance === 'row' && !isMobile;
  const runIdRef = useRef<string | undefined>(undefined);
  const [shouldShowFeedback, setShouldShowFeedback] = useState(false);
  // AI functionality hook
  const {
    isGeneratingAI,
    handleFieldManualEdit,
    handleGenerateWithAI,
    hasExistingDimensions,
    isAIGeneratedField,
    getTooltipText,
  } = useProductSizeWithAI({
    weight,
    depth,
    width,
    height,
    onChange,
    requiredAiProperties,
    asAside: appearance === 'column',
    onWeightsAndDimensionsGenerated: (response) => {
      runIdRef.current = response.run_id;
      setShouldShowFeedback(true);
      trackingAiGenerateUseContentClick({
        feature: AiFeature.AI_PRODUCT_WEIGHTS_AND_DIMENSIONS,
      });
    },
  });

  const {
    showFeedbackModal,
    handlePositiveFeedback,
    handleNegativeFeedback,
    closeFeedbackModal,
  } = useAiFeedback({
    feature: AiFeature.AI_PRODUCT_WEIGHTS_AND_DIMENSIONS,
    runIdRef: runIdRef,
  });
  const handleOnBlur = () => {
    setIsShowAlert(Number(weight) > 99);
  };

  const handleChange = ({
    target: { name, value },
  }: ChangeEvent<HTMLInputElement>) => {
    handleFieldManualEdit(name as SizeField, value);
    onChange({ name, value });
  };

  const fieldAppearance = showAlertNoWeightAndDimension ? 'warning' : 'neutral';

  const renderInputsWidthAndHeight = (
    <>
      <Grid.Column>
        <FormField label={t('products.detail.width')} id="width">
          {isGeneratingAI ? (
            <Input.Skeleton />
          ) : (
            <InputNumberNimbus
              aria-label={t('products.detail.width')}
              appearance={fieldAppearance}
              type="float"
              onChange={handleChange}
              append="cm"
              appendPosition="end"
              value={width}
              placeholder="30"
              name="width"
              aiGenerated={
                isAIGeneratedField('width') ||
                Boolean(aiGeneratedWeightsAndDimensions?.width)
              }
              disabled={isGeneratingAI}
            />
          )}
        </FormField>
        {onChangeToAllVariants && (
          <ApplyValuesToAllVariants
            value={width}
            onChange={onChangeToAllVariants('width', width)}
          />
        )}
      </Grid.Column>
      <Grid.Column>
        <FormField label={t('products.detail.height')} id="height">
          {isGeneratingAI ? (
            <Input.Skeleton />
          ) : (
            <InputNumberNimbus
              aria-label={t('products.detail.height')}
              appearance={fieldAppearance}
              type="float"
              onChange={handleChange}
              append="cm"
              appendPosition="end"
              value={height}
              placeholder="30"
              name="height"
              aiGenerated={
                isAIGeneratedField('height') ||
                Boolean(aiGeneratedWeightsAndDimensions?.height)
              }
              disabled={isGeneratingAI}
            />
          )}
        </FormField>
        {onChangeToAllVariants && (
          <ApplyValuesToAllVariants
            value={height}
            onChange={onChangeToAllVariants('height', height)}
          />
        )}
      </Grid.Column>
    </>
  );

  return (
    <Box data-style="stratus--product-sizes">
      <Card>
        <Card.Header>
          <Box display="flex" gap="2" alignItems="center">
            <Title as="h4">{t('products.detail.weightAndDimensions')}</Title>
            <GenerateWithAiLink
              onClick={handleGenerateWithAI}
              trackingFeature={AiFeature.AI_PRODUCT_WEIGHTS_AND_DIMENSIONS}
              disabled={
                !requiredAiProperties?.imageUrl ||
                isGeneratingAI ||
                hasExistingDimensions
              }
              tooltipText={getTooltipText()}
              tooltipMaxWidth="220px"
            />
          </Box>
        </Card.Header>
        <Card.Body {...aiNavigationAttributes}>
          <Stack wrap align="stretch" column>
            <Alert
              appearance="warning"
              show={isShowAlert}
              title={t('products.detail.alertWeightTitle')}
            >
              {t('products.detail.alertWeightDescription')}
            </Alert>
            <Stack.Item>
              <Text>{`${t('products.detail.infoWeightAndDimensions')}`}</Text>
            </Stack.Item>
            <Alert appearance="warning" show={showAlertNoWeightAndDimension}>
              {t('products.detail.alertNoWeightAndDimentions')}
            </Alert>
            <Stack.Item fill>
              <Grid>
                <Grid.Row>
                  <Grid.Column>
                    <WeighInput
                      appearance={fieldAppearance}
                      label={t('products.detail.weight')}
                      value={weight}
                      onChange={handleChange}
                      placeholder="0.14"
                      onBlur={handleOnBlur}
                      aiGenerated={
                        isAIGeneratedField('weight') ||
                        Boolean(aiGeneratedWeightsAndDimensions?.weight)
                      }
                      isLoading={isGeneratingAI}
                    />
                    {onChangeToAllVariants && (
                      <ApplyValuesToAllVariants
                        value={weight}
                        onChange={onChangeToAllVariants('weight', weight)}
                      />
                    )}
                  </Grid.Column>
                  <Grid.Column>
                    <FormField label={t('products.detail.depth')} id="depth">
                      {isGeneratingAI ? (
                        <Input.Skeleton />
                      ) : (
                        <InputNumberNimbus
                          aria-label={t('products.detail.depth')}
                          appearance={fieldAppearance}
                          type="float"
                          onChange={handleChange}
                          append="cm"
                          appendPosition="end"
                          value={depth}
                          placeholder="30"
                          name="depth"
                          aiGenerated={
                            isAIGeneratedField('depth') ||
                            Boolean(aiGeneratedWeightsAndDimensions?.depth)
                          }
                          disabled={isGeneratingAI}
                        />
                      )}
                    </FormField>
                    {onChangeToAllVariants && (
                      <ApplyValuesToAllVariants
                        value={depth}
                        onChange={onChangeToAllVariants('depth', depth)}
                      />
                    )}
                  </Grid.Column>
                  {isRowAppearance && renderInputsWidthAndHeight}
                </Grid.Row>
                {!isRowAppearance && (
                  <Grid.Row>{renderInputsWidthAndHeight}</Grid.Row>
                )}
              </Grid>
            </Stack.Item>
            {isGeneratingAI && (
              <Stack.Item>
                <AiLoadingState
                  loadingText={tCommon(
                    'aiGeneration.generatingWeightsAndDimensions',
                  )}
                />
              </Stack.Item>
            )}
            {shouldShowFeedback && (
              <FeatureFlag flag={AI_PRODUCT_FEEDBACK_ENABLED}>
                <Stack.Item>
                  <ActionableToast
                    message={tCommon(
                      'aiGeneration.weightsAndDimensionsGenerated',
                    )}
                    thumbsUpAriaLabel={tCommon(
                      'aiFeedbackModal.thumbsUpAriaLabel',
                    )}
                    thumbsDownAriaLabel={tCommon(
                      'aiFeedbackModal.thumbsDownAriaLabel',
                    )}
                    onPositiveFeedback={handlePositiveFeedback}
                    onNegativeFeedback={handleNegativeFeedback}
                    remainingAiUses={remainingAiUses}
                    onClose={() => setShouldShowFeedback(false)}
                  />
                </Stack.Item>
              </FeatureFlag>
            )}
          </Stack>
        </Card.Body>
        <Card.Footer>
          <HelpLink
            title={t('helpLink.weightCalculation')}
            linkURL={HELP_LINK_WEIGHT_CALCULATION_URL[language]}
            currentViewTracking={t('helpLink.weightCalculationTrackingName')}
            previousValue="product_details_weight_dimensions"
            appearance="neutral"
            textDecoration="underline"
            icon="external"
          />
        </Card.Footer>
      </Card>
      <AiFeedbackModal
        show={showFeedbackModal}
        title={t(
          'products.detail.seo.aiCategorySuggestions.feedbackModal.title',
        )}
        text={t('products.detail.seo.aiCategorySuggestions.feedbackModal.text')}
        feature={AiFeature.AI_PRODUCT_WEIGHTS_AND_DIMENSIONS}
        feedbackParams={{
          run_id: runIdRef.current,
          score: AI_FEEDBACK_SCORE.NEGATIVE,
        }}
        onDismiss={closeFeedbackModal}
      />
    </Box>
  );
}

export default ProductSizes;
